import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const DashboardQuickActions = ({ 
  recentPlanners = [], 
  onCreateNew, 
  onViewPlanner,
  className = ""
}) => {
  const navigate = useNavigate();

  const handleCreateNew = () => {
    if (onCreateNew) {
      onCreateNew();
    } else {
      navigate('/planner-setup-wizard');
    }
  };

  const handleViewPlanner = (plannerId) => {
    if (onViewPlanner) {
      onViewPlanner(plannerId);
    } else {
      navigate(`/planner-preview?id=${plannerId}`);
    }
  };

  const quickActions = [
    {
      id: 'create-new',
      title: 'Create New Planner',
      description: 'Start designing your planner',
      icon: 'Plus',
      color: 'primary',
      action: handleCreateNew
    },
    {
      id: 'templates',
      title: 'Browse Templates',
      description: 'Explore pre-made planner designs',
      icon: 'Layout',
      color: 'secondary',
      action: () => navigate('/planner-setup-wizard?tab=templates')
    },
    {
      id: 'recent',
      title: 'Recent Planners',
      description: 'Continue working on saved planners',
      icon: 'Clock',
      color: 'accent',
      action: () => {} // Handled by recent planners section
    }
  ];

  const formatDate = (dateString) => {
    try {
      return new Date(dateString)?.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const formatPlannerType = (configData) => {
    if (!configData || typeof configData !== 'object') {
      return 'Custom';
    }

    const features = [];
    
    if (configData?.yearlyOverview) features?.push('Yearly');
    if (configData?.monthPlanner) features?.push('Monthly');
    if (configData?.weekPlanner) features?.push('Weekly');
    if (configData?.dailyPlanner?.enabled) features?.push('Daily');
    if (configData?.meetingNotes) features?.push('Meetings');
    if (configData?.notes) features?.push('Notes');
    
    if (features?.length === 0) {
      return 'Custom Planner';
    }
    
    if (features?.length > 2) {
      return `${features?.[0]} + ${features?.length - 1} more`;
    }
    
    return features?.join(' + ');
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {quickActions?.map((action) => (
          <div
            key={action?.id}
            className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
            onClick={action?.action}
          >
            <div className="flex items-start space-x-4">
              <div className={`
                w-12 h-12 rounded-lg flex items-center justify-center
                group-hover:scale-105 transition-transform duration-200
                ${action?.color === 'primary' ? 'bg-primary text-primary-foreground' : ''}
                ${action?.color === 'secondary' ? 'bg-secondary text-secondary-foreground' : ''}
                ${action?.color === 'accent' ? 'bg-accent text-accent-foreground' : ''}
              `}>
                <Icon name={action?.icon} size={24} />
              </div>
              
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-foreground mb-1">
                  {action?.title}
                </h3>
                <p className="font-body text-sm text-muted-foreground">
                  {action?.description}
                </p>
              </div>
              
              <Icon 
                name="ArrowRight" 
                size={16} 
                className="text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-all duration-200" 
              />
            </div>
          </div>
        ))}
      </div>
      {/* Recent Planners Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold text-foreground">
            Recent Planners
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/user-account-dashboard?tab=all-planners')}
            iconName="ArrowRight"
            iconPosition="right"
            iconSize={14}
          >
            View All
          </Button>
        </div>

        {recentPlanners?.length > 0 ? (
          <div className="space-y-3">
            {recentPlanners?.slice(0, 5)?.map((planner) => (
              <div
                key={planner?.id}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors duration-200 cursor-pointer group"
                onClick={() => handleViewPlanner(planner?.id)}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                    <Icon name="FileText" size={16} color="var(--color-muted-foreground)" />
                  </div>
                  
                  <div>
                    <h4 className="font-body font-medium text-foreground group-hover:text-primary transition-colors">
                      {planner?.name || 'Untitled Planner'}
                    </h4>
                    <p className="font-caption text-xs text-muted-foreground">
                      {formatPlannerType(planner?.type)} • {formatDate(planner?.lastModified)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className={`
                    px-2 py-1 rounded-full text-xs font-caption
                    ${planner?.status === 'completed' 
                      ? 'bg-success/10 text-success' 
                      : planner?.status === 'draft' ?'bg-warning/10 text-warning' :'bg-muted text-muted-foreground'
                    }
                  `}>
                    {planner?.status || 'draft'}
                  </span>
                  
                  <Icon 
                    name="MoreHorizontal" 
                    size={16} 
                    className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" 
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="FileText" size={24} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="font-heading font-medium text-foreground mb-2">
              No planners yet
            </h3>
            <p className="font-body text-sm text-muted-foreground mb-4">
              Create your first planner to get started
            </p>
            <Button
              variant="outline"
              onClick={handleCreateNew}
              iconName="Plus"
              iconPosition="left"
            >
              Create Your First Planner
            </Button>
          </div>
        )}
      </div>
      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="font-heading text-2xl font-bold text-primary mb-1">
            {recentPlanners?.length}
          </div>
          <div className="font-caption text-xs text-muted-foreground">
            Total Planners
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="font-heading text-2xl font-bold text-success mb-1">
            {recentPlanners?.filter(p => p?.status === 'completed')?.length}
          </div>
          <div className="font-caption text-xs text-muted-foreground">
            Completed
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="font-heading text-2xl font-bold text-warning mb-1">
            {recentPlanners?.filter(p => p?.status === 'draft')?.length}
          </div>
          <div className="font-caption text-xs text-muted-foreground">
            In Progress
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4 text-center">
          <div className="font-heading text-2xl font-bold text-accent mb-1">
            {recentPlanners?.filter(p => {
              const lastWeek = new Date();
              lastWeek?.setDate(lastWeek?.getDate() - 7);
              return new Date(p.lastModified) > lastWeek;
            })?.length}
          </div>
          <div className="font-caption text-xs text-muted-foreground">
            This Week
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardQuickActions;